const getConfig = (root, kind = 'config') => {
    const selector = `script[data-kind="${kind}"][type="application/json"]`;
    return [...root.querySelectorAll(selector)].map((script) => JSON.parse(script.textContent));
};

const paymentType = 'paypal:card-fields';

Alpine.data('SevenPaymentPaypalCardFields', (element) => ({
    config: getConfig(element).at(-1),
    instance: false,
    isSupported: false,
    name: element.dataset.name,

    state: {
        isFormValid: false,
        NameField: { touched: false, isValid: false },
        CVVField: { touched: false, isValid: false },
        ExpiryField: { touched: false, isValid: false },
        NumberField: { touched: false, isValid: false },
    },

    fields: {},
    redirectUrl: null,

    // provided via model
    // @see https://paypal.github.io/paypal-web/3.34.0/PayPalCheckout.html#createPayment
    paymentOptions: {
        amount: 0,
    },

    async init() {
        this.tokenType = `${paymentType}:${this.config.id}`;
        this.instance = await new Promise((resolve) => {
            document.addEventListener('seven:payment:paypal:client:init', (event) => resolve(event.detail));
        });

        const style = {
            input: this._getInputStyle(),
            '.invalid': this._getInputStyle('is-invalid'),
        };

        const stateChange = (eventName) => (event) => {
            this.state = {
                isFormValid: event.isFormValid,
                NameField: {
                    touched: this.state.NameField.touched || (event.emittedBy === 'name' && eventName === 'blur'),
                    ...event.fields.cardNameField,
                },
                CVVField: {
                    touched: this.state.CVVField.touched || (event.emittedBy === 'cvv' && eventName === 'blur'),
                    ...event.fields.cardCvvField,
                },
                ExpiryField: {
                    touched: this.state.ExpiryField.touched || (event.emittedBy === 'expiry' && eventName === 'blur'),
                    ...event.fields.cardExpiryField,
                },
                NumberField: {
                    touched: this.state.NumberField.touched || (event.emittedBy === 'number' && eventName === 'blur'),
                    ...event.fields.cardNumberField,
                },
            };
        };

        const cardFields = this.instance.paypal.CardFields({
            style,
            createOrder: this._createOrder.bind(this),
            onApprove: this._onApprove.bind(this),
            inputEvents: {
                onChange: stateChange('change'),
                onBlur: stateChange('blur'),
                onFocus: stateChange('focus'),
                onInputSubmitRequest: (e) => this._dispatchEvent('inputsubmitrequest', e),
            },
        });

        if (cardFields?.isEligible()) {
            this.isSupported = true;
            const fields = Array.from(element.querySelectorAll('.paypal-card-field'));

            for (const field of fields) {
                const cardField = cardFields[field.dataset.name]({
                    placeholder: field.dataset.placeholder,
                });

                cardField.render(field);
                this.fields[field.dataset.name] = cardField;
            }

            this._dispatchEvent('init', {
                id: this.config.id,
                instance: cardFields,
                tokenType: this.tokenType,
                submit: async () => {
                    if (this.state.isFormValid) {
                        return cardFields.submit();
                    }

                    const fields = ['NameField', 'NumberField', 'CVVField', 'ExpiryField'].reverse();

                    for (const field of fields) {
                        if (!this.state[field]?.isValid) {
                            await this.fields[field]?.focus();
                        }
                    }

                    return Promise.reject(new Error('Form is not valid'));
                },
            });
        }
    },

    /*
    * Create a fake form-control to be able to get the styles from it.
    * After styles are computed, the fake input is removed.
    */
    _getInputStyle(className) {
        const fake = document.createElement('input');
        fake.classList.add('form-control');
        fake.classList.add('paypal-form-control');

        if (className) {
            fake.classList.add(className);
        }

        document.body.append(fake);

        const props = [
            'appearance',
            'color',
            'direction',
            'font',
            'font-family',
            'font-size',
            'font-size-adjust',
            'font-stretch',
            'font-style',
            'font-variant',
            'font-variant-alternates',
            'font-variant-caps',
            'font-variant-east-asian',
            'font-variant-ligatures',
            'font-variant-numeric',
            'font-weight',
            'padding',
            'padding-bottom',
            'padding-left',
            'padding-right',
            'padding-top',
            'letter-spacing',
            'line-height',
            'opacity',
            'outline',
            'text-shadow',
            'transition',
            '-moz-appearance',
            '-moz-osx-font-smoothing',
            '-moz-tap-highlight-color',
            '-moz-transition',
            '-webkit-appearance',
            '-webkit-osx-font-smoothing',
            '-webkit-tap-highlight-color',
            '-webkit-transition',
        ];

        const style = window.getComputedStyle(fake);
        const inputStyle = Array.from(style)
            .filter((property) => props.includes(property))
            .reduce(
                (acc, property) => ({
                    ...acc,
                    fontFamily: style.getPropertyValue('font-family'),
                    [property]: style.getPropertyValue(property),
                }),
                {},
            );

        fake.remove();
        return inputStyle;
    },

    async _createOrder() {
        const {token, redirectUrl} = await this.instance.createOrder(this.config.id);
        this.redirectUrl = redirectUrl;
        return token;
    },

    async _onApprove() {
        if (this.redirectUrl) {
            window.location.href = this.redirectUrl;
        }
    },

    _dispatchEvent(name, { cancelable, bubbles, ...detail } = {}) {
        return element.dispatchEvent(new CustomEvent(`seven:payment:paypal:cardfields:${name}`, {
            detail,
            bubbles: bubbles ?? true,
            cancelable: cancelable ?? true,
        }));
    },

}));
